import { getContext, setContext, type Snippet } from 'svelte';
import {
  derived,
  get,
  type Readable,
  readable,
  type Writable,
  writable,
} from 'svelte/store';

import { BannerVariant, type BannerVariantType } from '../banner';
import { uniqueId } from '../unique-id';
import { pausableProgress } from './pausable-progress';

/** Internal notification context. */
export interface NotifyContext {
  state: NotifyState;
  notify: Notify;
}

export interface NotifyOptions {
  persist?: boolean;
  onDismiss?: VoidFunction;
  content?: Snippet;
}

export type NotifyFunction = (
  title: string,
  message?: string,
  options?: NotifyOptions
) => { dismiss: VoidFunction };

/** Notification sender. */
export interface Notify {
  /** Push an info notification. */
  info: NotifyFunction;
  /** Push a warning notification. */
  warn: NotifyFunction;
  /** Push a danger notification. */
  danger: NotifyFunction;
  /** Push a success notification. */
  success: NotifyFunction;
  /** Push an in-progress notification. */
  pending: NotifyFunction;
}

/** Internal notification state. */
export interface NotifyState {
  notifications: Readable<Notification[]>;
  pageIsVisible: Writable<boolean>;
}

/** Internal notification item state and actions. */
export interface Notification {
  id: string;
  title: string;
  message: string | undefined;
  variant: BannerVariantType;
  progress: Readable<number>;
  content?: Snippet;
  pause: () => void;
  resume: () => void;
  dismiss: () => void;
}

const NotifyContextKey = Symbol('notify-context');
const NotificationDuration = 4000;

/** Create the internal notification state and context object. */
export const createNotifyContext = (): NotifyContext => {
  const notifications = writable<Notification[]>([]);
  const pageIsVisible = writable(true);
  const pausedID = writable<string | undefined>();
  const unsubscribeByID = new Map<string, () => void>();

  const add =
    (variant: BannerVariantType) =>
    (
      title: string,
      message?: string,
      options?: NotifyOptions
    ): { dismiss: VoidFunction } => {
      const id = uniqueId('notification');
      const isPaused = derived(
        [pageIsVisible, pausedID],
        ([$pageIsVisible, $pausedID]) => $pausedID === id || !$pageIsVisible
      );

      const pause = () => setPaused(id, true);
      const resume = () => setPaused(id, false);
      const dismiss = () => {
        options?.onDismiss?.();
        remove(id);
      };
      const { progress, unsubscribe } = options?.persist
        ? { progress: readable(1), unsubscribe: () => undefined }
        : pausableProgress({
            isPaused,
            totalDuration: NotificationDuration,
            onComplete: dismiss,
          });

      const notification = {
        id,
        variant,
        title,
        message,
        content: options?.content,
        progress,
        pause,
        resume,
        dismiss,
      };

      unsubscribeByID.set(id, unsubscribe);
      notifications.set([...get(notifications), notification]);

      return { dismiss };
    };

  const remove = (id: string) => {
    unsubscribeByID.get(id)?.();
    unsubscribeByID.delete(id);
    notifications.set(get(notifications).filter((item) => item.id !== id));
  };

  const setPaused = (id: string, isPaused: boolean) => {
    if (isPaused) {
      pausedID.set(id);
    } else if (get(pausedID) === id) {
      pausedID.set(undefined);
    }
  };

  const context: NotifyContext = {
    state: { notifications, pageIsVisible },
    notify: {
      info: add(BannerVariant.Info),
      warn: add(BannerVariant.Warning),
      danger: add(BannerVariant.Danger),
      success: add(BannerVariant.Success),
      pending: add(BannerVariant.Pending),
    },
  };

  return context;
};

/** Provide notification state to a component tree. */
export const provideNotify = (context = createNotifyContext()): void => {
  setContext(NotifyContextKey, context);
};

const useNotifyContext = (): NotifyContext => {
  const context = getContext<NotifyContext | undefined>(NotifyContextKey);

  if (!context) {
    throw new Error('Usage: No context from `provideNotify` available');
  }

  return context;
};

/** Get access to the notification sender in a component. */
export const useNotify = (): Notify => {
  return useNotifyContext().notify;
};

/** Get access to the internal notification state. */
export const useNotifyState = (): NotifyState => {
  return useNotifyContext().state;
};
